import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight, faFileExport, faFileImport, faGripLinesVertical, faSave, faTrash, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FileUploadModule, FileUploader } from 'ng2-file-upload';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Group } from 'src/app/models/group';
import { IListLayoutAccessibility } from 'src/app/models/interfaces/list-layout-accessibility.interface';
import { ListLayout } from 'src/app/models/list-layout';
import { ListLayoutAccessibility } from 'src/app/models/list-layout-accessibility';
import { User } from 'src/app/models/user';
import { DefaultHttpService } from 'src/app/services/default-http.service';
import { GroupsService } from 'src/app/services/groups.service';
import { ListLayoutService } from 'src/app/services/list-layout.service';
import { MemoryService } from 'src/app/services/memory.service';
import { UsersService } from 'src/app/services/users.service';

@Component({
    selector: 'app-list-layout-boxes-accessibility-form',
    templateUrl: './list-layout-boxes-accessibility-form.component.html',
    styleUrls: ['./list-layout-boxes-accessibility-form.component.scss'],
    imports: [CdkDropListGroup, MatTabsModule, CdkDropList, CdkDrag, FaIconComponent, CdkDragHandle, MatButtonModule, FileUploadModule]
})
export class ListLayoutBoxesAccessibilityFormComponent extends DefaultHttpService implements OnInit {
    public faTrash = faTrash;
    public faUser = faUser;
    public faUsers = faUsers;
    public faAngleDoubleLeft = faAngleDoubleLeft;
    public faAngleDoubleRight = faAngleDoubleRight;
    public faGripLinesVertical = faGripLinesVertical;
    public faFileExport = faFileExport;
    public faSave = faSave;
    public faFileimport = faFileImport;

    _listLayout: ListLayout;

    @Input()
    public set listLayout(value: ListLayout) {
        this._listLayout = value;
        this.loadInformations();
        this.processAccessibilities();
    }
    public get listLayout(): ListLayout {
        return this._listLayout;
    }

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    @Output()
    public accessibilitiesUpdated: EventEmitter<ListLayoutAccessibility[]> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    users: User[];

    groups: Group[];

    accessibilities: ListLayoutAccessibility[];

    unusedUsers: User[];

    unusedGroups: Group[];

    @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

    // Accès au enfant de type fileToUpload
    @ViewChild('fileToUpload', { static: true })
    fileToUploadVariable: ElementRef;

    // Composant FileUploaderCustom
    public uploader: FileUploader = null;

    constructor(
        private listLayoutService: ListLayoutService,
        private usersService: UsersService,
        private groupsService: GroupsService,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    ngOnInit(): void {
        this.uploader = new FileUploader({
            url: `${this.baseUrl}/api/organizations/${this.organizationId}/listlayouts/${this._listLayout.id}/accessibilities/import`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${this.connectedUser.token.accessToken}`,
            allowedFileType: ['xls'],
            queueLimit: 1,
        });

        this.uploader.onAfterAddingFile = (fileItem) => {
            fileItem.withCredentials = false;
        };

        this.uploader.onCompleteAll = () => {
            this.loadInformations();
        };
    }

    public onFileSelected() {
        this.uploader.uploadAll();
    }

    loadInformations(): void {
        this.listLayoutService.getAccessibilities(this._listLayout.id).subscribe((x) => {
            this.accessibilities = x;

            this.listLayoutService.getAccessibilitiesForbidden(this._listLayout.id).subscribe((forbidden) => {
                // on récupère tous les utilisateurs
                this.usersService.List(null, null, null, null).subscribe((x) => {
                    this.users = x.value
                        .filter((x) => !forbidden.map((f) => f.userId).includes(x.id))
                        .sort((a, b) => {
                            return a.displayName < b.displayName ? -1 : 1;
                        });
                    this.determinedUnusedUsers();
                    this.processAccessibilities();
                });

                // on récupère les groupes
                this.groupsService.List(null, null, null, null).subscribe((x) => {
                    this.groups = x.value
                        .filter((x) => !forbidden.map((f) => f.groupId).includes(x.id))
                        .sort((a, b) => {
                            return a.name < b.name ? -1 : 1;
                        });
                    this.determinedUnusedGroups();
                    this.processAccessibilities();
                });
            });
        });
    }

    /**
     * Determine les utilisateurs qui n'ont pas accès à la mise en page.
     */
    determinedUnusedUsers() {
        if (this.users && this.accessibilities) {
            this.unusedUsers = [...this.users];
            this.accessibilities.forEach((x) => {
                if (x.userId) {
                    let index = this.unusedUsers.findIndex((y) => y.id === x.userId);
                    this.unusedUsers.splice(index, 1);
                }
            });
        }
    }

    /**
     * Determine les groupes qui n'ont pas accès à la mise en page.
     */
    determinedUnusedGroups() {
        if (this.groups && this.accessibilities) {
            this.unusedGroups = [...this.groups];
            this.accessibilities.forEach((x) => {
                if (x.groupId) {
                    let index = this.unusedGroups.findIndex((y) => y.id === x.groupId);
                    this.unusedGroups.splice(index, 1);
                }
            });
        }
    }

    /**
     * Determine les groupes et utilisateur qui ont accès à la mise en page.
     */
    processAccessibilities() {
        if (this.groups && this.users && this.accessibilities) {
            this.accessibilities.forEach((x) => {
                if (x.userId) {
                    let user = this.users.find((y) => y.id === x.userId);
                    x.userName = user.displayName;
                }

                if (x.groupId) {
                    let group = this.groups.find((y) => y.id === x.groupId);
                    x.groupName = group.name;
                }
            });

            this.sortAccessibilities();
        }
    }

    /**
     * Supprime un accès des listes
     */
    removeAccessibility(accessibility: IListLayoutAccessibility, index: number) {
        if (accessibility.userId) {
            let targetData = this.getUser(accessibility.userId);
            transferArrayItem<User>([targetData], this.unusedUsers, index, this.unusedUsers.length);
        } else {
            let targetData = this.getGroup(accessibility.groupId);
            transferArrayItem<Group>([targetData], this.unusedGroups, index, this.unusedGroups.length);
        }
    }

    /**
     * Ajout un utilisateur à la liste
     * */
    addUserAccessibility(userId: string, index: number) {
        const accessibility: ListLayoutAccessibility[] = [
            {
                id: null,
                organizationId: this._listLayout.organizationId,
                listLayoutId: this._listLayout.id,
                userId: userId,
                groupId: null,
                userName: this.getUserName(userId),
                groupName: null,
            },
        ];

        transferArrayItem<IListLayoutAccessibility>(accessibility, this.accessibilities, -1, index);
    }

    /**
     * Ajout un groupe à la liste
     */
    addGroupAccessibility(groupId: string, index: number) {
        const accessibility: ListLayoutAccessibility[] = [
            {
                id: null,
                organizationId: this._listLayout.organizationId,
                listLayoutId: this._listLayout.id,
                userId: null,
                groupId: groupId,
                userName: null,
                groupName: this.getGroupName(groupId),
            },
        ];

        transferArrayItem<IListLayoutAccessibility>(accessibility, this.accessibilities, -1, index);
    }

    /**
     * Gestion des movements des boites
     */
    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            if (event.container.id === 'users' || event.container.id === 'groups') {
                let selectedData = (event.previousContainer.data as any[] as IListLayoutAccessibility[])[event.previousIndex];
                this.removeAccessibility(selectedData, event.previousIndex);
                event.previousContainer.data.splice(event.previousIndex, 1); // You will have to remove previous item yourself
            } else if (event.container.id === 'accessibilities') {
                if (event.previousContainer.id === 'users') {
                    this.addUserAccessibility((event.previousContainer.data[event.previousIndex] as any as User).id, event.currentIndex);
                } else {
                    this.addGroupAccessibility((event.previousContainer.data[event.previousIndex] as any as Group).id, event.currentIndex);
                }

                event.previousContainer.data.splice(event.previousIndex, 1); // You will have to remove previous item yourself
            }

            this.sort();
        }

        this.sort();
    }

    private sort() {
        this.sortAccessibilities();
        this.sortUnusedGroups();
        this.sortUnusedUsers();
    }

    private sortAccessibilities() {
        let sortedAccessibilities = this.accessibilities.sort((a, b) => {
            if (a.groupName && b.groupName) {
                return a.groupName < b.groupName ? -1 : 1;
            } else if (a.userName && b.userName) {
                return a.userName < b.userName ? -1 : 1;
            } else if (a.groupName) {
                return -1;
            } else {
                return 1;
            }
        });

        this.accessibilities = [...sortedAccessibilities];
    }

    private sortUnusedUsers() {
        let sortedUsers = this.unusedUsers.sort((a, b) => {
            return a.displayName < b.displayName ? -1 : 1;
        });

        this.unusedUsers = [...sortedUsers];
    }

    private sortUnusedGroups() {
        let sortedGroups = this.unusedGroups.sort((a, b) => {
            return a.name < b.name ? -1 : 1;
        });

        this.unusedGroups = [...sortedGroups];
    }

    private getUserName(userId: string) {
        const user = this.users.find((x) => x.id == userId);
        return user.displayName;
    }

    private getUser(userId: string) {
        const user = this.users.find((x) => x.id == userId);
        return user;
    }

    private getGroupName(groupId: string) {
        const group = this.groups.find((x) => x.id == groupId);
        return `${group.name}`;
    }

    private getGroup(groupId: string) {
        const group = this.groups.find((x) => x.id == groupId);
        return group;
    }

    public save(): void {
        this.accessibilitiesUpdated.emit(this.accessibilities);
    }

    public onExported() {
        this.listLayoutService
            .exportAccessibilities(this._listLayout.id)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = 'Mises en page par défaut.xlsx';
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    /**
     * Supprime toutes les accessibilité.
     */
    public moveAllInUnusedGroupsOrUsers(): void {
        this.accessibilities.forEach((accessibility) => {
            if (accessibility.groupId !== null) {
                this.unusedGroups.push(this.getGroup(accessibility.groupId));
            } else {
                this.unusedUsers.push(this.getUser(accessibility.userId));
            }
        });

        this.accessibilities = [];
    }

    /**
     * Ajout toutes les accessibilité.
     */
    public moveAllInAccessibility(): void {
        if (this.tabGroup.selectedIndex === 0) {
            this.unusedUsers.forEach((user) => {
                const accessibility: ListLayoutAccessibility = {
                    id: null,
                    organizationId: this._listLayout.organizationId,
                    listLayoutId: this._listLayout.id,
                    userId: user.id,
                    groupId: null,
                    userName: this.getUserName(user.id),
                    groupName: null,
                };

                this.accessibilities.push(accessibility);
            });

            this.unusedUsers = [];
            this.sort();
        } else {
            this.unusedGroups.forEach((group) => {
                const boxAccessibility: ListLayoutAccessibility = {
                    id: null,
                    organizationId: this._listLayout.organizationId,
                    listLayoutId: this._listLayout.id,
                    userId: null,
                    groupId: group.id,
                    userName: null,
                    groupName: this.getGroupName(group.id),
                };

                this.accessibilities.push(boxAccessibility);
            });

            this.unusedGroups = [];
            this.sort();
        }
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
